import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { default as React } from "react";

import { cn } from "@trip/utils";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center rounded-lg  text-sm font-medium duration-150 ease-out delay-100 transition-all disabled:pointer-events-none disabled:opacity-50",
    "outline-prune-500 focus-visible:outline-2 data-[state=open]:outline-2",
  ),
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        circle: "rounded-full p-2",
        default: "h-10 px-4 py-2",
        lg: "h-11 px-6 text-base",
        sm: "h-9 px-3",
        link: "text-base",
      },
      variant: {
        default:
          "bg-white shadow-field hover:shadow-field-hover disabled:text-muted/40 disabled:opacity-100",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        frost: [
          "rounded-full",
          "text-white bg-blend-color-burn backdrop-blur-md hover:text-neutral-200",
          "shadow-frost",
          "[background-image:linear-gradient(0deg,rgba(255,255,255,0.24),rgba(255,255,255,0.24)),linear-gradient(0deg,transparent,#B2B2B2)]",
        ],
        ghost:
          "hover:bg-black/10 hover:text-accent-foreground disabled:opacity-20",
        link: "text-prune-500 hover:underline",
        outline:
          "border-[1.5px] border-prune-500 text-prune-500 hover:bg-accent",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        sunset:
          "rounded-full [background-image:var(--sunset-left-right)] text-white shadow-button-emboss hover:shadow-button-emboss-hover",
      },
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ className, size, variant }))}
      ref={ref}
      {...props}
    />
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
